import React, { useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import api from '../api/axios'
import { Lock, Bell, Loader2, Eye, EyeOff } from 'lucide-react'

export default function ClientSettingsPage() {
  const { user, refreshUser } = useAuth()

  // Password change
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [savingPassword, setSavingPassword] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')

  // Notification settings
  const [completionEmail, setCompletionEmail] = useState(true)
  const [subscriptionAlert, setSubscriptionAlert] = useState(true)
  const [queryEmail, setQueryEmail] = useState(true)
  const [weeklyUsageEmail, setWeeklyUsageEmail] = useState(true)
  const [hasSubscription, setHasSubscription] = useState(false)
  const [loadingNotif, setLoadingNotif] = useState(true)
  const [notifSuccess, setNotifSuccess] = useState('')

  const fetchNotifSettings = async () => {
    setLoadingNotif(true)
    try {
      const [notifRes, clientRes] = await Promise.all([
        api.get('/clients/me/notification-settings'),
        api.get('/clients/me')
      ])
      setCompletionEmail(notifRes.data.completionEmail ?? true)
      setSubscriptionAlert(notifRes.data.subscriptionAlertEmail ?? true)
      setQueryEmail(notifRes.data.queryEmail ?? true)
      setWeeklyUsageEmail(notifRes.data.weeklyUsageEmail ?? true)
      setHasSubscription(!!clientRes.data.activeSubscription)
    } catch {}
    setLoadingNotif(false)
  }

  useEffect(() => { fetchNotifSettings() }, [])

  const handleChangePassword = async () => {
    setPasswordError('')
    setPasswordSuccess('')
    if (!currentPassword || !newPassword || !confirmPassword) {
      return setPasswordError('All fields are required')
    }
    if (newPassword.length < 6) {
      return setPasswordError('New password must be at least 8 characters')
    }
    if (newPassword !== confirmPassword) {
      return setPasswordError('New passwords do not match')
    }
    setSavingPassword(true)
    try {
      await api.patch('/auth/change-password', { currentPassword, newPassword })
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setPasswordSuccess('Password changed successfully')
      setTimeout(() => setPasswordSuccess(''), 4000)
      await refreshUser()
    } catch (e: any) {
      setPasswordError(e.response?.data?.error || 'Failed to change password')
    }
    setSavingPassword(false)
  }

  const saveNotifSetting = async (key: string, value: boolean) => {
    try {
      await api.patch('/clients/me/notification-settings', {
        completionEmail: key === 'completionEmail' ? value : completionEmail,
        subscriptionAlertEmail: key === 'subscriptionAlertEmail' ? value : subscriptionAlert,
        queryEmail: key === 'queryEmail' ? value : queryEmail,
        weeklyUsageEmail: key === 'weeklyUsageEmail' ? value : weeklyUsageEmail
      })
      setNotifSuccess('Saved')
      setTimeout(() => setNotifSuccess(''), 1500)
    } catch {
      setNotifSuccess('Failed to save')
    }
  }

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold text-navy-900">Settings</h1>
        <p className="text-sm text-gray-400 mt-0.5">Manage your account preferences</p>
      </div>

      {user?.mustChangePassword && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 flex items-start gap-3">
          <span className="text-orange-500 text-lg">⚠</span>
          <div>
            <p className="text-sm font-semibold text-orange-700">Please change your password</p>
            <p className="text-xs text-orange-500 mt-0.5">You are using a temporary password. Please update it before continuing.</p>
          </div>
        </div>
      )}

      {/* Change Password */}
      <div className="bg-white dark:bg-[#16304f] rounded-2xl shadow-sm border border-gray-100 dark:border-[#1e3a5f] p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 bg-orange-50 rounded-xl flex items-center justify-center">
            <Lock size={16} className="text-orange-500" />
          </div>
          <div>
            <h2 className="font-display font-semibold text-navy-900">Change Password</h2>
            <p className="text-xs text-gray-400">Keep your account secure</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="label">Current Password</label>
            <div className="relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                className="input pr-10"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
              />
              <button onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showCurrent ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          <div>
            <label className="label">New Password</label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                className="input pr-10"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Min 8 characters"
              />
              <button onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          <div>
            <label className="label">Confirm New Password</label>
            <input
              type="password"
              className="input"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Repeat new password"
            />
          </div>

          {passwordError && <p className="text-red-500 text-sm">{passwordError}</p>}
          {passwordSuccess && <p className="text-green-600 text-sm">{passwordSuccess}</p>}

          <button
            onClick={handleChangePassword}
            disabled={savingPassword}
            className="btn-primary flex items-center gap-2">
            {savingPassword && <Loader2 size={14} className="animate-spin" />}
            Change Password
          </button>
        </div>
      </div>

      {/* Email Notifications */}
      <div className="bg-white dark:bg-[#16304f] rounded-2xl shadow-sm border border-gray-100 dark:border-[#1e3a5f] p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 bg-orange-50 rounded-xl flex items-center justify-center">
            <Bell size={16} className="text-orange-500" />
          </div>
          <div>
            <h2 className="font-display font-semibold text-navy-900">Email Notifications</h2>
            <p className="text-xs text-gray-400">Choose what emails you receive</p>
          </div>
        </div>

        {loadingNotif ? (
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <Loader2 size={14} className="animate-spin" /> Loading...
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-50">
              <div>
                <p className="text-sm font-medium text-navy-800">Job Completion Emails</p>
                <p className="text-xs text-gray-400 mt-0.5">Receive email when your job is completed</p>
              </div>
              <button
                onClick={() => { setCompletionEmail(!completionEmail); saveNotifSetting('completionEmail', !completionEmail) }}
                className={`relative w-10 h-[22px] rounded-full transition-colors flex-shrink-0 ${completionEmail ? 'bg-orange-500' : 'bg-gray-200'}`}>
                <span className={`absolute top-[3px] w-4 h-4 bg-white rounded-full shadow-sm transition-all ${completionEmail ? 'left-[22px]' : 'left-[3px]'}`} />
              </button>
            </div>

            {hasSubscription && (
              <div className="flex items-center justify-between py-3 border-b border-gray-50">
                <div>
                  <p className="text-sm font-medium text-navy-800">Subscription Alert Emails</p>
                  <p className="text-xs text-gray-400 mt-0.5">Get notified before your subscription expires</p>
                </div>
                <button
                  onClick={() => { setSubscriptionAlert(!subscriptionAlert); saveNotifSetting('subscriptionAlertEmail', !subscriptionAlert) }}
                  className={`relative w-10 h-[22px] rounded-full transition-colors flex-shrink-0 ${subscriptionAlert ? 'bg-orange-500' : 'bg-gray-200'}`}>
                  <span className={`absolute top-[3px] w-4 h-4 bg-white rounded-full shadow-sm transition-all ${subscriptionAlert ? 'left-[22px]' : 'left-[3px]'}`} />
                </button>
              </div>
            )}

            <div className="flex items-center justify-between py-3 border-b border-gray-50">
              <div>
                <p className="text-sm font-medium text-navy-800">Query from Studio</p>
                <p className="text-xs text-gray-400 mt-0.5">Get notified when we have a question about your job</p>
              </div>
              <button onClick={() => { setQueryEmail(!queryEmail); saveNotifSetting('queryEmail', !queryEmail) }}
                className={`relative w-10 h-[22px] rounded-full transition-colors flex-shrink-0 ${queryEmail ? 'bg-orange-500' : 'bg-gray-200'}`}>
                <span className={`absolute top-[3px] w-4 h-4 bg-white rounded-full shadow-sm transition-all ${queryEmail ? 'left-[22px]' : 'left-[3px]'}`} />
              </button>
            </div>

            <div className="flex items-center justify-between py-3">
              <div>
                <p className="text-sm font-medium text-navy-800">Weekly Usage Summary</p>
                <p className="text-xs text-gray-400 mt-0.5">Receive a weekly email showing your jobs used and remaining</p>
              </div>
              <button onClick={() => { setWeeklyUsageEmail(!weeklyUsageEmail); saveNotifSetting('weeklyUsageEmail', !weeklyUsageEmail) }}
                className={`relative w-10 h-[22px] rounded-full transition-colors flex-shrink-0 ${weeklyUsageEmail ? 'bg-orange-500' : 'bg-gray-200'}`}>
                <span className={`absolute top-[3px] w-4 h-4 bg-white rounded-full shadow-sm transition-all ${weeklyUsageEmail ? 'left-[22px]' : 'left-[3px]'}`} />
              </button>
            </div>

            {notifSuccess && <p className="text-xs text-green-600 flex items-center gap-1">✓ {notifSuccess}</p>}
          </div>
        )}
      </div>
    </div>
  )
}